import { cn } from '@repo/ui'
import { useEffect, useRef, useState } from 'react'
import type { ChangeEvent, KeyboardEvent, ReactNode } from 'react'

export function InlineEditableText({
  value,
  onSave,
  editing,
  onEditingChange,
  textClassName,
  ariaLabel,
  renderDisplay
}: {
  value: string
  onSave: (next: string) => Promise<void>
  editing: boolean
  onEditingChange: (editing: boolean) => void
  textClassName: string
  ariaLabel: string
  renderDisplay: (start: () => void) => ReactNode
}) {
  const [draft, setDraft] = useState(value)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  // Set when the user cancels (Escape) so the blur that follows does not save.
  const revertingRef = useRef(false)

  // Seed the draft and focus when entering edit mode. Depending only on `editing` keeps a parent
  // re-render (new `value` identity) from clobbering an in-progress edit.
  useEffect(() => {
    if (editing) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setDraft(value)
      setError(null)
      inputRef.current?.focus()
      inputRef.current?.select()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing])

  async function save() {
    const trimmed = draft.trim()
    if (trimmed.length === 0 || trimmed === value) {
      setDraft(value)
      setError(null)
      onEditingChange(false)
      return
    }
    setSaving(true)
    setError(null)
    try {
      await onSave(trimmed)
      onEditingChange(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save')
      inputRef.current?.focus()
    } finally {
      setSaving(false)
    }
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault()
      void save()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      revertingRef.current = true
      setDraft(value)
      setError(null)
      onEditingChange(false)
    }
  }

  function handleBlur() {
    if (saving) return
    if (revertingRef.current) {
      revertingRef.current = false
      return
    }
    void save()
  }

  if (!editing) {
    return <>{renderDisplay(() => onEditingChange(true))}</>
  }

  return (
    <div className="flex flex-col gap-1">
      {/* An inline grid stacks a hidden sizer over the input so the field grows to fit its content. */}
      <div className="inline-grid max-w-full items-center overflow-hidden">
        <span aria-hidden className={cn(textClassName, 'col-start-1 row-start-1 px-px whitespace-pre')}>
          {draft || value || 'Untitled'}
        </span>
        <input
          ref={inputRef}
          value={draft}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          disabled={saving}
          aria-label={ariaLabel}
          className={cn(
            textClassName,
            'col-start-1 row-start-1 w-full border-0 bg-transparent px-px outline-none focus:outline-none focus-visible:outline-none'
          )}
        />
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  )
}
